import React, { useState } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AnalyzeForm } from './components/AnalyzeForm';
import { ScoreDashboard } from './components/ScoreDashboard';
import { AnalysisReport } from './components/AnalysisReport';
import { RateLimitBanner } from './components/RateLimitBanner';
import { ExportMenu } from './components/ExportMenu';
import { analyzeHtml, scrapeUrl, RateLimitError } from './lib/api';
import type { AppState, RenderDiff } from './types';

const initialState: AppState = {
  status: 'idle',
  result: null,
  error: null,
  rateLimit: null,
  lastCsrHtml: null,
};

export default function App() {
  const [state, setState] = useState<AppState>(initialState);

  const handleErr = (err: unknown) => {
    if (err instanceof RateLimitError) {
      setState({ ...initialState, status: 'rate-limited', rateLimit: err.info });
    } else {
      const message = err instanceof Error ? err.message : 'Request failed';
      setState({ ...initialState, status: 'error', error: message });
    }
  };

  const handleHtml = async (html: string) => {
    setState({ ...initialState, status: 'analyzing' });
    try {
      const result = await analyzeHtml(html);
      setState({ ...initialState, status: 'complete', result });
    } catch (err) {
      handleErr(err);
    }
  };

  const handleUrl = async (url: string) => {
    setState({ ...initialState, status: 'scraping' });
    try {
      const { csrHtml, ssrHtml, sourceUrl, sourceTitle } = await scrapeUrl(url);
      setState((s) => ({ ...s, status: 'analyzing' }));
      const result = await analyzeHtml(csrHtml, { sourceUrl, sourceTitle, ssrHtml });
      setState({ ...initialState, status: 'complete', result, lastCsrHtml: csrHtml });
    } catch (err) {
      handleErr(err);
    }
  };

  const handleManualSsrDiff = (newDiff: RenderDiff) => {
    setState((s) => {
      if (!s.result) return s;
      return { ...s, result: { ...s.result, renderDiff: newDiff } };
    });
  };

  const busy = state.status === 'scraping' || state.status === 'analyzing';
  const busyLabel =
    state.status === 'scraping' ? 'Fetching page…'
    : state.status === 'analyzing' ? 'Analyzing…'
    : undefined;

  return (
    <>
      <Header />

      <main>
        <nav aria-label="Breadcrumb" className="breadcrumbs">
          <div className="container-narrow">
            <ol>
              <li><a href="https://theseoconsultant.ai/">Home</a></li>
              <li><a href="https://theseoconsultant.ai/ai-seo-tools/">AI SEO Tools</a></li>
              <li aria-current="page">Semantic HTML Analyzer</li>
            </ol>
          </div>
        </nav>
        <section className="section">
          <div className="container-narrow">
            <p className="eyebrow" style={{ marginBottom: 16 }}>Technical SEO · Semantic HTML5</p>
            <h1 className="hero-headline">
              See what <em>crawlers see</em> when they read your HTML.
            </h1>
            <p className="lead" style={{ marginBottom: 40 }}>
              Drop in a URL or paste rendered HTML. We score it against modern semantic standards,
              accessibility rules, and structured-data best practices — then tell you what to fix.
            </p>

            <AnalyzeForm
              busy={busy}
              busyLabel={busyLabel}
              onSubmitHtml={handleHtml}
              onSubmitUrl={handleUrl}
            />

            {state.rateLimit && <RateLimitBanner info={state.rateLimit} />}

            {state.error && !state.rateLimit && (
              <div
                role="alert"
                style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  background: 'var(--score-low-wash)',
                  border: '1px solid rgba(179,58,58,0.25)',
                  color: 'var(--score-low)',
                  borderRadius: 4,
                  fontSize: 14,
                }}
              >
                {state.error}
              </div>
            )}
          </div>
        </section>

        {state.status === 'complete' && state.result && (
          <section className="section section-paper-2">
            <div className="container animate-in">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {state.result.sourceUrl && (
                    <>
                      <span className="eyebrow">Analyzed</span>
                      <p style={{ margin: '6px 0 0', fontSize: 15, color: 'var(--ink-2)' }}>
                        {state.result.sourceTitle && (
                          <strong style={{ fontWeight: 600 }}>{state.result.sourceTitle} · </strong>
                        )}
                        <a href={state.result.sourceUrl} target="_blank" rel="noopener">
                          {state.result.sourceUrl}
                        </a>
                      </p>
                    </>
                  )}
                </div>
                <ExportMenu result={state.result} />
              </div>
              <ScoreDashboard result={state.result} />
              <AnalysisReport
                result={state.result}
                csrHtml={state.lastCsrHtml}
                onManualSsrDiff={handleManualSsrDiff}
              />
              <div className="print-byline" aria-hidden>
                Created by Richard Wong / The SEO Consultant.ai LLC ·{' '}
                <a href="https://theseoconsultant.ai">theseoconsultant.ai</a>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}
